import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Pagination } from '@/components/ui/pagination'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import type { Voucher } from '@/types/api'
import { ChevronDown, Edit2, Eye, EyeOff, Plus, Search, Trash2 } from 'lucide-react'
import React from 'react'

interface VoucherListProps {
  vouchers: Voucher[]
  isLoading: boolean
  currentPage: number
  totalPages: number
  totalItems: number
  itemsPerPage: number
  searchTerm: string
  statusFilter: string
  typeFilter: string
  onSearchChange: (_value: string) => void
  onStatusFilterChange: (_value: string) => void
  onTypeFilterChange: (_value: string) => void
  onPageChange: (_page: number) => void
  onItemsPerPageChange: (_value: number) => void
  onAddVoucher: () => void
  onEditVoucher: (_voucher: Voucher) => void
  onDeleteVoucher: (_id: string) => void
  onToggleStatus: (_id: string) => void
  onRefresh?: () => void
}

export const VoucherList: React.FC<VoucherListProps> = ({
  vouchers,
  isLoading,
  currentPage,
  totalPages,
  totalItems,
  itemsPerPage,
  searchTerm,
  statusFilter,
  typeFilter,
  onSearchChange,
  onStatusFilterChange,
  onTypeFilterChange,
  onPageChange,
  onItemsPerPageChange,
  onAddVoucher,
  onEditVoucher,
  onDeleteVoucher,
  onToggleStatus,
}) => {
  const formatPrice = (amount: number) => {
    return `Rp ${amount.toLocaleString('id-ID')}`
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('id-ID')
  }

  const getVoucherStatus = (voucher: Voucher): string => {
    if (!voucher.is_active) return 'inactive'
    const now = new Date()
    const validUntil = new Date(voucher.valid_until)
    if (validUntil < now) return 'expired'
    return 'active'
  }

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800'
      case 'inactive':
        return 'bg-gray-100 text-gray-800'
      case 'expired':
        return 'bg-red-100 text-red-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this voucher?')) {
      return
    }
    onDeleteVoucher(id)
  }

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Vouchers</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex gap-4">
              <Skeleton className="h-10 flex-1" />
              <Skeleton className="h-10 w-32" />
              <Skeleton className="h-10 w-32" />
            </div>
            <div className="space-y-2">
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Vouchers</CardTitle>
          <div className="flex items-center justify-between">
            <div className="flex items-center flex-1">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <Input
                  placeholder="Search vouchers..."
                  value={searchTerm}
                  onChange={(e) => onSearchChange(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            <div className="flex items-center space-x-2 ml-4">
              <div className="relative">
                <select
                  value={statusFilter}
                  onChange={(e) => onStatusFilterChange(e.target.value)}
                  className="flex h-10 rounded-md border border-input bg-background pl-3 pr-8 py-2 text-sm ring-offset-background appearance-none"
                >
                  <option value="all">All Status</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="expired">Expired</option>
                </select>
                <ChevronDown className="pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              </div>

              <div className="relative">
                <select
                  value={typeFilter}
                  onChange={(e) => onTypeFilterChange(e.target.value)}
                  className="flex h-10 rounded-md border border-input bg-background pl-3 pr-8 py-2 text-sm ring-offset-background appearance-none"
                >
                  <option value="all">All Types</option>
                  <option value="fixed">Fixed</option>
                  <option value="percentage">Percentage</option>
                </select>
                <ChevronDown className="pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              </div>

              <Button onClick={onAddVoucher} className="bg-primary-600 hover:bg-primary-700">
                <Plus className="mr-2 h-4 w-4" />
                Add Voucher
              </Button>
            </div>
          </div>
        </CardHeader>

        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Code</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Discount Value</TableHead>
                  <TableHead>Valid Period</TableHead>
                  <TableHead>Usage</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {!vouchers || vouchers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="h-24 text-center">
                      No vouchers found
                    </TableCell>
                  </TableRow>
                ) : (
                  vouchers.map((voucher) => {
                    const status = getVoucherStatus(voucher)
                    return (
                      <TableRow key={voucher.id} className="hover:bg-gray-50">
                        <TableCell className="font-mono font-semibold">{voucher.code}</TableCell>
                        <TableCell>{voucher.name}</TableCell>
                        <TableCell>
                          <Badge variant="secondary" className="bg-primary-100 text-primary-800">
                            {voucher.type === 'fixed' ? 'Fixed' : 'Percentage'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {voucher.type === 'fixed'
                            ? formatPrice(voucher.discount_value)
                            : `${voucher.discount_value}%`}
                          {voucher.max_discount && voucher.type === 'percentage' && (
                            <div className="text-xs text-gray-500">
                              Max: {formatPrice(voucher.max_discount)}
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">
                            <div>From: {formatDate(voucher.valid_from)}</div>
                            <div>Until: {formatDate(voucher.valid_until)}</div>
                          </div>
                        </TableCell>
                        <TableCell>
                          {voucher.used_count} / {voucher.usage_limit || '∞'}
                        </TableCell>
                        <TableCell>
                          <Badge className={getStatusBadge(status)}>
                            {status.charAt(0).toUpperCase() + status.slice(1)}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => onEditVoucher(voucher)}
                              className="text-primary-600 hover:text-primary-900"
                            >
                              <Edit2 className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => onToggleStatus(voucher.id)}
                              className="text-amber-600 hover:text-amber-900"
                              title={voucher.is_active ? 'Deactivate' : 'Activate'}
                            >
                              {voucher.is_active ? (
                                <Eye className="h-4 w-4" />
                              ) : (
                                <EyeOff className="h-4 w-4" />
                              )}
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(voucher.id)}
                              className="text-red-600 hover:text-red-900"
                              disabled={voucher.used_count > 0}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    )
                  })
                )}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-4">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={totalItems}
                itemsPerPage={itemsPerPage}
                onPageChange={onPageChange}
                onItemsPerPageChange={onItemsPerPageChange}
              />
            </div>
          )}
        </CardContent>
      </Card>
    </>
  )
}
